import { useDispatch, useSelector } from "react-redux";
import { useCallback } from "react";
import {
  fetchStorytellers,
  fetchPublishedStorytellers,
  fetchStorytellerById,
  createStoryteller,
  updateStoryteller,
  deleteStoryteller,
  publishStoryteller,
  unpublishStoryteller,
  fetchStorytellerStats,
  setFilters,
  clearFilters,
  clearError,
  clearCurrentStoryteller,
} from "../store/storytellersSlice";

// Custom hook for storytellers
export const useStorytellers = () => {
  const dispatch = useDispatch();
  const {
    storytellers,
    publishedStorytellers,
    currentStoryteller,
    stats,
    loading,
    error,
    pagination,
    filters,
  } = useSelector((state) => state.storytellers);

  // Action creators
  const actions = {
    fetchStorytellers: useCallback(
      (params) => dispatch(fetchStorytellers(params)),
      [dispatch]
    ),
    fetchPublishedStorytellers: useCallback(
      (params) => dispatch(fetchPublishedStorytellers(params)),
      [dispatch]
    ),
    fetchStorytellerById: useCallback(
      (id) => dispatch(fetchStorytellerById(id)),
      [dispatch]
    ),
    createStoryteller: useCallback(
      (data) => dispatch(createStoryteller(data)),
      [dispatch]
    ),
    updateStoryteller: useCallback(
      (id, data) => dispatch(updateStoryteller({ id, data })),
      [dispatch]
    ),
    deleteStoryteller: useCallback(
      (id) => dispatch(deleteStoryteller(id)),
      [dispatch]
    ),
    publishStoryteller: useCallback(
      (id) => dispatch(publishStoryteller(id)),
      [dispatch]
    ),
    unpublishStoryteller: useCallback(
      (id) => dispatch(unpublishStoryteller(id)),
      [dispatch]
    ),
    fetchStorytellerStats: useCallback(
      () => dispatch(fetchStorytellerStats()),
      [dispatch]
    ),
    setFilters: useCallback(
      (filters) => dispatch(setFilters(filters)),
      [dispatch]
    ),
    clearFilters: useCallback(() => dispatch(clearFilters()), [dispatch]),
    clearError: useCallback(() => dispatch(clearError()), [dispatch]),
    clearCurrentStoryteller: useCallback(
      () => dispatch(clearCurrentStoryteller()),
      [dispatch]
    ),
  };

  return {
    // State
    storytellers,
    publishedStorytellers,
    currentStoryteller,
    stats,
    loading,
    error,
    pagination,
    filters,
    // Actions
    ...actions,
  };
};

// Hook for pagination
export const usePagination = () => {
  const { pagination, fetchStorytellers, filters } = useStorytellers();

  const goToPage = useCallback(
    (page) => {
      fetchStorytellers({
        ...filters,
        page,
        limit: pagination?.limit || 10,
      });
    },
    [fetchStorytellers, filters, pagination?.limit]
  );

  const changePageSize = useCallback(
    (limit) => {
      fetchStorytellers({
        ...filters,
        page: 1,
        limit,
      });
    },
    [fetchStorytellers, filters]
  );

  return {
    ...pagination,
    goToPage,
    changePageSize,
  };
};

// Hook for filters
export const useFilters = () => {
  const { filters, setFilters, clearFilters, fetchStorytellers, pagination } =
    useStorytellers();

  const updateFilters = useCallback(
    (newFilters) => {
      const updatedFilters = { ...filters, ...newFilters };
      setFilters(updatedFilters);
      fetchStorytellers({
        ...updatedFilters,
        page: 1,
        limit: pagination?.limit || 10,
      });
    },
    [filters, setFilters, fetchStorytellers, pagination?.limit]
  );

  const resetFilters = useCallback(() => {
    clearFilters();
    fetchStorytellers({
      page: 1,
      limit: pagination?.limit || 10,
    });
  }, [clearFilters, fetchStorytellers, pagination?.limit]);

  return {
    filters,
    updateFilters,
    resetFilters,
  };
};
